import React, { useCallback, useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import BaseMap from './BaseMap'
import RadiusCircle from './RadiusCircle'
import { theme } from '../../config/theme'

const SaleShowMap = ({ 
  className = '',
  saleLocation,
  eventLocation,
  radius,
  otherSales = [],
  currentSaleId
}) => {
  const eventMarker = useRef(null)
  const saleMarker = useRef(null)
  const otherSalesMarkers = useRef([])
  const [mapInstance, setMapInstance] = useState(null)

  // Handle map ready
  const handleMapReady = useCallback((mapInstance) => {
    setMapInstance(mapInstance)
  }, [])

  // Initialize markers when map is ready
  useEffect(() => {
    if (!mapInstance) return

    // Remove existing other sales markers
    otherSalesMarkers.current.forEach(marker => marker.remove())
    otherSalesMarkers.current = []

    // Add event marker (blue) if we have event location
    if (eventLocation) {
      const { latitude, longitude } = eventLocation
      const lat = parseFloat(latitude)
      const lng = parseFloat(longitude)

      if (!isNaN(lat) && !isNaN(lng)) {
        if (eventMarker.current) {
          eventMarker.current.remove()
        }
        
        eventMarker.current = new mapboxgl.Marker({ color: theme.colors.primary[500] })
          .setLngLat([lng, lat])
          .addTo(mapInstance)
      }
    }

    // Add other sales markers (red) - exclude current sale
    otherSales.forEach(sale => {
      if (sale.id === currentSaleId) return // Skip current sale
      if (!sale.location?.latitude || !sale.location?.longitude) return

      const lng = parseFloat(sale.location.longitude)
      const lat = parseFloat(sale.location.latitude)
      
      if (!isNaN(lng) && !isNaN(lat)) {
        const marker = new mapboxgl.Marker({ color: theme.colors.red[500] })
          .setLngLat([lng, lat])
          .addTo(mapInstance)

        otherSalesMarkers.current.push(marker)
      }
    })

    // Add sale marker (green) - this is the main focus
    if (saleLocation) {
      const lat = parseFloat(saleLocation.latitude)
      const lng = parseFloat(saleLocation.longitude)

      if (!isNaN(lat) && !isNaN(lng)) {
        if (saleMarker.current) {
          saleMarker.current.remove()
        }
        
        saleMarker.current = new mapboxgl.Marker({ color: theme.colors.green[500] })
          .setLngLat([lng, lat])
          .addTo(mapInstance)

        // Calculate bounds to include all markers but prioritize current sale
        const allCoordinates = [[lng, lat]] // Start with current sale
        
        // Add all other sales coordinates
        otherSales.forEach(sale => {
          if (sale.id !== currentSaleId && sale.location?.latitude && sale.location?.longitude) {
            const otherLng = parseFloat(sale.location.longitude)
            const otherLat = parseFloat(sale.location.latitude)
            if (!isNaN(otherLng) && !isNaN(otherLat)) {
              allCoordinates.push([otherLng, otherLat])
            }
          }
        })
        
        // Add event center if available
        if (eventLocation?.latitude && eventLocation?.longitude) {
          const eventLng = parseFloat(eventLocation.longitude)
          const eventLat = parseFloat(eventLocation.latitude)
          if (!isNaN(eventLng) && !isNaN(eventLat)) {
            allCoordinates.push([eventLng, eventLat])
          }
        }
        
        if (allCoordinates.length > 1) {
          // Multiple points - fit bounds but ensure current sale is visible
          const lngs = allCoordinates.map(coord => coord[0])
          const lats = allCoordinates.map(coord => coord[1])
          
          const bounds = [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)]
          ]
          
          mapInstance.fitBounds(bounds, {
            padding: 60,
            maxZoom: 13 // Reasonable max zoom to keep current sale prominent
          })
        } else {
          // Only current sale - center on it
          const baseZoom = radius ? Math.max(12, 16 - Math.log2(radius)) : 15
          mapInstance.flyTo({
            center: [lng, lat],
            zoom: baseZoom
          })
        }
      }
    }
  }, [mapInstance, saleLocation, eventLocation, radius, otherSales, currentSaleId])

  // Get initial map center - prioritize sale location
  const initialCenter = React.useMemo(() => {
    if (saleLocation?.latitude && saleLocation?.longitude) {
      return [parseFloat(saleLocation.longitude), parseFloat(saleLocation.latitude)]
    }
    if (eventLocation?.latitude && eventLocation?.longitude) {
      return [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]
    }
    return [-98.5795, 39.8283] // Default center of US
  }, [saleLocation, eventLocation])

  const initialZoom = saleLocation ? 15 : 12

  return (
    <div className={className}>
      <BaseMap
        center={initialCenter}
        zoom={initialZoom}
        onMapReady={handleMapReady}
      >
        {eventLocation && (
          <RadiusCircle
            center={[parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]}
            radius={radius}
            visible={!!eventLocation}
          />
        )}
      </BaseMap>
    </div>
  )
}

export default SaleShowMap